
import { defineComponent, onBeforeMount, reactive } from 'vue'

// @components
import TheModal from '@/common/components/UI/Modal/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'

// @sub-components
import PretripsGroup from './PretripsGroup.vue'
import GroupByDate from './GroupByDate.vue'

// @types
import type { PreTripForCompleteDay, UpdatePreTripPayload } from '@/core/types/tms-graph.types'
import type { TripComment, TripTMS } from '@/new-services/types/tms'

// @realisations
import { tmsGraph } from '@/core/realisations/tms-graph.realisation'
import tmsAPI from '@/new-services/tms.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

type TripsGroupByDate = {
    date: string
    pretrips: Array<PreTripForCompleteDay>
}

const ISSUE_BY_LOGIST_REASON_ID = 39
const ISSUE_BY_WAREHOUSE_REASON_ID = 40
const EARLY_SEND_REASON_ID = 41

export default defineComponent({
    components: {
        TheModal,
        Loader,
        TheButton,
        PretripsGroup,
        GroupByDate,
        NewInput,
        NewSelect,
    },
    props: {
        isCanComplete: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_can_complete: false,
        })

        const pretrips_state = reactive({
            is_loading: false,
            is_failed: false,
            items: [] as Array<PreTripForCompleteDay>,
            groups: [] as Array<TripsGroupByDate>
        })

        const update_pretrip_state = reactive({
            is_opened: false,
            is_loading: false,
            pretrip: null as PreTripForCompleteDay | null,
            date: null as string | null,
            reasonId: null as number | null,
            comment: null as string | null,
            tripId: null as number | null,
        })

        const update_pretrip_form$ = useVuelidate(
            {
                date: { required },
                reasonId: { required },
                comment: { required: requiredIf(() => update_pretrip_state.reasonId === ISSUE_BY_LOGIST_REASON_ID || update_pretrip_state.reasonId === ISSUE_BY_WAREHOUSE_REASON_ID), },
                tripId: { required: requiredIf(() => update_pretrip_state.reasonId === EARLY_SEND_REASON_ID) }
            },
            update_pretrip_state
        )

        const reasons_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TripComment[],
        })

        const trips_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TripTMS[],
        })

        const loadTrips = async () => {
            resetTrips()
            update_pretrip_state.tripId = null
            
            try {
                trips_state.is_loading = true

                const response = await tmsAPI.getTrips({
                    page: 1,
                    limit: 9999,
                    dateFrom: update_pretrip_state.date as string,
                    dateTo: update_pretrip_state.date as string
                })

                trips_state.list = response.data.data

                trips_state.is_failed = false
            } catch (error) {
                console.error(error)
                trips_state.is_failed = true
            } finally {
                trips_state.is_loading = false
            }
        }

        const resetTrips = () => {
            trips_state.is_loading = false
            trips_state.is_failed = false
            trips_state.list = []
        }

        const getWeekPeriod = (): { dateFrom: string; dateTo: string; } => {
            const todayDate = new Date()
            const weekAgoDate = new Date()

            weekAgoDate.setDate(todayDate.getDate() - 7)

            return {
                dateFrom: weekAgoDate.toISOString().split("T")[0],
                dateTo: todayDate.toISOString().split("T")[0]
            }
        }

        const loadPretrips = async () => {
            try {
                pretrips_state.is_loading = true

                const response = await tmsGraph.getPretripsForCompleteDay()

                response.data.forEach((item: PreTripForCompleteDay) => {
                    const groupIndex = pretrips_state.groups.findIndex((group) => group.date === item.date)

                    if (groupIndex >= 0) {
                        pretrips_state.groups[groupIndex].pretrips.push(item)
                    } else {
                        pretrips_state.groups.push({ date: item.date, pretrips: [item] })
                    }
                })

                pretrips_state.groups.sort((a, b) => {
                    return new Date(b.date).getTime() - new Date(a.date).getTime()
                })

                pretrips_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении предрейсов произошла ошибка' })
                pretrips_state.is_failed = true
            } finally {
                pretrips_state.is_loading = false
            }
        }

        const loadReasons = async () => {
            try {
                reasons_state.is_loading = true

                const response = await tmsAPI.getPreTripComments()
                reasons_state.list = response.data.data

                reasons_state.is_failed = false
            } catch (error) {
                console.error(error)
                reasons_state.is_failed = true
            } finally {
                reasons_state.is_loading = false
            }
        }

        onBeforeMount(() => {
            state.is_can_complete = props.isCanComplete

            if (!state.is_can_complete) {
                loadReasons()
                loadPretrips()
            }
        })

        const getFormattedDate = (): string => {
            const date = new Date(Date.now())
            return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0')
        }

        const onUpdatePretrip = (pretrip: PreTripForCompleteDay) => {
            update_pretrip_state.pretrip = {...pretrip}
            update_pretrip_state.date = getFormattedDate()
            update_pretrip_state.is_opened = true
        }

        const onUpdatePretripCancel = () => {
            if (update_pretrip_state.is_loading) return

            update_pretrip_state.is_opened = false
            resetUpdatePretripForm()
        }

        const resetUpdatePretripForm = () => {
            update_pretrip_state.pretrip = null
            update_pretrip_state.date = null
            update_pretrip_state.comment = null
            update_pretrip_state.reasonId = null
            update_pretrip_state.tripId = null
            update_pretrip_form$.value.$reset()
        }

        const onPretripUpdated = (pretrip_id: number) => {
            const groupIndex = pretrips_state.groups.findIndex((group) => group.pretrips.map((pretrip) => pretrip.id).includes(pretrip_id))

            if (groupIndex >= 0) {
                const pretripIndex = pretrips_state.groups[groupIndex].pretrips.findIndex((pretrip) => pretrip.id === pretrip_id)
                pretrips_state.groups[groupIndex].pretrips.splice(pretripIndex, 1)

                if (pretrips_state.groups[groupIndex].pretrips.length === 0) {
                    pretrips_state.groups.splice(groupIndex, 1)
                }
            }
        }

        const onUpdatePretripSubmit = async () => {
            if (update_pretrip_state.is_loading || !update_pretrip_state.pretrip) return

            update_pretrip_form$.value.$touch()
            if (update_pretrip_form$.value.$invalid) return

            try {
                update_pretrip_state.is_loading = true

                const payload: UpdatePreTripPayload = {
                    commentId: update_pretrip_state.reasonId as number,
                    date: update_pretrip_state.date as string,
                    commentInfo: update_pretrip_state.comment
                }

                await tmsGraph.updatePreTrip(update_pretrip_state.pretrip.id, payload)
                onPretripUpdated(update_pretrip_state.pretrip.id)

                update_pretrip_state.is_opened = false
                resetUpdatePretripForm()

                notifications.notify({ title: 'Предрейс сохранен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании предрейса произошла ошибка',
                })
            } finally {
                update_pretrip_state.is_loading = false
            }
        }

        const onCancel = () => {
            emit('cancel')
        }

        const onDateChange = (date: string) => {
            if (update_pretrip_state.reasonId && update_pretrip_state.reasonId === EARLY_SEND_REASON_ID) {
                if (Boolean(date)) {
                    const selectedDate = new Date(date)

                    if (selectedDate.getFullYear() >= 2000) {
                        loadTrips()
                    }
                }
            }
        }

        const onReasonChange = (reasonId: number | null) => {
            resetTrips()

            update_pretrip_state.comment = null
            update_pretrip_state.tripId = null

            if (reasonId) {
                if (reasonId === EARLY_SEND_REASON_ID) {
                    update_pretrip_state.date = null
                    return
                }
            }
        }

        return {
            ISSUE_BY_LOGIST_REASON_ID,
            ISSUE_BY_WAREHOUSE_REASON_ID,
            EARLY_SEND_REASON_ID,
            state,
            pretrips_state,
            reasons_state,
            trips_state,
            update_pretrip_state,
            update_pretrip_form$,
            loadPretrips,
            onUpdatePretrip,
            onUpdatePretripCancel,
            onUpdatePretripSubmit,
            onCancel,
            onReasonChange,
            onDateChange,
        }
    }
})
